// src/pages/Appli/AppliAdmin/clubs-admin/components/ClubCells.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaEnvelope,
  FaGlobe,
  FaPen,
  FaSave,
  FaPhoneAlt,
  FaTrash,
  FaUsers,
} from "react-icons/fa";

import { CLUB_LOGO_FALLBACK } from "../data/clubsAdmin.data";

import {
  formatMemberCount,
  normalizeWebsiteUrl,
} from "../utils/clubsAdmin.utils";

export function DepartmentBadge({
  children,
}) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </span>
  );
}

export function ClubLogo({
  src,
  alt,
}) {
  const [
    imageSource,
    setImageSource,
  ] = useState(
    src ||
      CLUB_LOGO_FALLBACK,
  );

  useEffect(() => {
    setImageSource(
      src ||
        CLUB_LOGO_FALLBACK,
    );
  }, [src]);

  return (
    <img
      src={imageSource}
      alt={
        alt ||
        "Logo du club"
      }
      onError={() =>
        setImageSource(
          CLUB_LOGO_FALLBACK,
        )
      }
      className="h-11 w-11 rounded-xl bg-white object-cover ring-1 ring-slate-200 dark:ring-slate-700"
      loading="lazy"
    />
  );
}

export function ContactBlock({
  club,
}) {
  const hasContact = Boolean(
    club?.tel_club ||
      club?.email_club ||
      club?.site_club,
  );

  if (!hasContact) {
    return (
      <p className="text-xs text-slate-400">
        Aucun contact renseigné
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {club?.tel_club ? (
        <a
          href={`tel:${club.tel_club}`}
          className="flex items-start gap-2 text-[13px] text-slate-700 hover:text-indigo-600 dark:text-slate-200 dark:hover:text-indigo-300"
        >
          <FaPhoneAlt className="mt-0.5 shrink-0 text-slate-400" />

          <span className="break-all">
            {club.tel_club}
          </span>
        </a>
      ) : null}

      {club?.email_club ? (
        <a
          href={`mailto:${club.email_club}`}
          className="flex items-start gap-2 text-[13px] text-indigo-600 hover:underline dark:text-indigo-300"
        >
          <FaEnvelope className="mt-0.5 shrink-0" />

          <span className="break-all">
            {club.email_club}
          </span>
        </a>
      ) : null}

      {club?.site_club ? (
        <a
          href={normalizeWebsiteUrl(
            club.site_club,
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2 break-all text-[13px] text-indigo-600 hover:underline dark:text-indigo-300"
        >
          <FaGlobe className="mt-0.5 shrink-0" />

          <span className="break-all">
            {club.site_club}
          </span>
        </a>
      ) : null}
    </div>
  );
}

function normalizeAdherentCount(
  value,
) {
  const numericValue =
    Number(value);

  if (
    !Number.isInteger(
      numericValue,
    ) ||
    numericValue < 0
  ) {
    return 0;
  }

  return numericValue;
}

export function AdherentCountEditor({
  club,
  saving = false,
  onSave,
  className = "",
}) {
  const savedCount =
    normalizeAdherentCount(
      club?.nombre_adherents,
    );

  const [value, setValue] =
    useState(
      String(savedCount),
    );

  useEffect(() => {
    setValue(
      String(savedCount),
    );
  }, [savedCount]);

  const validation =
    useMemo(() => {
      const trimmedValue =
        String(value).trim();

      if (!trimmedValue) {
        return {
          isValid: false,
          numericValue: null,
        };
      }

      const numericValue =
        Number(trimmedValue);

      return {
        isValid:
          Number.isInteger(
            numericValue,
          ) &&
          numericValue >= 0,
        numericValue,
      };
    }, [value]);

  const isDirty =
    validation.isValid &&
    validation.numericValue !==
      savedCount;

  const handleSave =
    async () => {
      if (
        saving ||
        !isDirty ||
        !validation.isValid
      ) {
        return;
      }

      const success =
        await onSave?.(
          club?.id,
          validation.numericValue,
        );

      if (!success) {
        setValue(
          String(savedCount),
        );
      }
    };

  const handleKeyDown = (
    event,
  ) => {
    if (
      event.key === "Enter"
    ) {
      event.preventDefault();
      handleSave();
    }

    if (
      event.key === "Escape"
    ) {
      event.preventDefault();

      setValue(
        String(savedCount),
      );
    }
  };

  const hasLicencies =
    savedCount > 0;

  return (
    <div
      className={[
        "rounded-2xl border p-2.5 transition",
        hasLicencies
          ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-500/10"
          : "border-amber-200 bg-amber-50/70 dark:border-amber-500/20 dark:bg-amber-500/10",
        className,
      ].join(" ")}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={[
            "flex h-7 w-7 items-center justify-center rounded-lg",
            hasLicencies
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
          ].join(" ")}
        >
          <FaUsers className="text-xs" />
        </span>

        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500 dark:text-slate-400">
            Licenciés
          </p>

          <p className="text-[10px] text-slate-400">
            Mise à jour rapide
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          value={value}
          onChange={(event) =>
            setValue(
              event.target.value,
            )
          }
          onKeyDown={
            handleKeyDown
          }
          onWheel={(event) =>
            event.currentTarget.blur()
          }
          disabled={saving}
          aria-label={`Nombre de licenciés du club ${
            club?.nom_club || ""
          }`}
          className={[
            "h-9 min-w-0 flex-1 rounded-xl border bg-white px-2 text-center text-sm font-bold text-slate-900 outline-none transition",
            "dark:bg-slate-950 dark:text-white",
            validation.isValid
              ? "border-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700"
              : "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/10",
          ].join(" ")}
        />

        <button
          type="button"
          onClick={handleSave}
          disabled={
            saving ||
            !isDirty ||
            !validation.isValid
          }
          title={
            isDirty
              ? "Enregistrer le nombre de licenciés"
              : "Aucune modification"
          }
          aria-label={`Enregistrer le nombre de licenciés du club ${
            club?.nom_club || ""
          }`}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
        >
          {saving ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
          ) : (
            <FaSave />
          )}
        </button>
      </div>

      {!validation.isValid ? (
        <p className="mt-1.5 text-[10px] font-medium text-red-600 dark:text-red-400">
          Saisissez un entier positif ou nul.
        </p>
      ) : isDirty ? (
        <p className="mt-1.5 text-[10px] font-medium text-indigo-600 dark:text-indigo-300">
          Modification non enregistrée
        </p>
      ) : null}
    </div>
  );
}

function getMemberCountState(
  count,
  loading,
) {
  if (loading) {
    return {
      hasMembers: false,
      isZero: false,
      isUnknown: false,
    };
  }

  if (
    count === null ||
    count === undefined
  ) {
    return {
      hasMembers: false,
      isZero: false,
      isUnknown: true,
    };
  }

  const numericCount =
    Number(count);

  return {
    hasMembers:
      Number.isFinite(
        numericCount,
      ) &&
      numericCount > 0,

    isZero:
      Number.isFinite(
        numericCount,
      ) &&
      numericCount === 0,

    isUnknown:
      !Number.isFinite(
        numericCount,
      ),
  };
}

function MemberCountIndicator({
  count,
  loading,
  hasMembers,
}) {
  if (loading) {
    return (
      <span
        className="flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-bold text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-200"
        aria-label="Chargement du nombre de membres"
      >
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 dark:border-slate-600 dark:border-t-slate-200" />
      </span>
    );
  }

  return (
    <span
      className={[
        "flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
        hasMembers
          ? "bg-white/20 text-white ring-1 ring-white/25"
          : "bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700",
      ].join(" ")}
    >
      {formatMemberCount(
        count,
      )}
    </span>
  );
}

export function MembersButton({
  club,
  count,
  loading,
  onMembers,
  compact = false,
  className = "",
}) {
  const {
    hasMembers,
    isUnknown,
  } =
    getMemberCountState(
      count,
      loading,
    );

  const accessibleCount =
    loading
      ? "chargement du nombre de membres"
      : isUnknown
        ? "nombre de membres inconnu"
        : `${Number(
            count,
          )} membre${
            Number(count) > 1
              ? "s"
              : ""
          }`;

  const buttonClasses =
    hasMembers
      ? [
          "border-[#013E8E]",
          "bg-[#013E8E]",
          "text-white",
          "shadow-sm",
          "hover:border-[#003772]",
          "hover:bg-[#003772]",
          "dark:border-blue-500",
          "dark:bg-blue-600",
          "dark:hover:border-blue-400",
          "dark:hover:bg-blue-500",
        ].join(" ")
      : [
          "border-slate-300",
          "bg-slate-100",
          "text-slate-600",
          "hover:border-slate-400",
          "hover:bg-slate-200",
          "hover:text-slate-800",
          "dark:border-slate-700",
          "dark:bg-slate-800",
          "dark:text-slate-300",
          "dark:hover:border-slate-600",
          "dark:hover:bg-slate-700",
          "dark:hover:text-white",
        ].join(" ");

  return (
    <button
      type="button"
      onClick={() =>
        onMembers(club)
      }
      className={[
        "inline-flex h-10 items-center justify-center gap-2 rounded-2xl border px-3 text-sm font-semibold transition",
        buttonClasses,
        className,
      ].join(" ")}
      title={`Voir les membres rattachés — ${accessibleCount}`}
      aria-label={`Voir les membres rattachés au club ${
        club?.nom_club || ""
      }, ${accessibleCount}`}
    >
      <FaUsers />

      {!compact ? (
        <span>Membres</span>
      ) : null}

      <MemberCountIndicator
        count={count}
        loading={loading}
        hasMembers={
          hasMembers
        }
      />
    </button>
  );
}

export function ClubActionButtons({
  club,

  memberCount,
  memberCountLoading,

  onMembers,
  onEdit,
  onDelete,

  deletingId,
  showMembers = true,
}) {
  const deleting =
    deletingId === club?.id;

  return (
    <div className="flex flex-wrap justify-center gap-2">
      {showMembers ? (
        <MembersButton
          club={club}
          count={memberCount}
          loading={
            memberCountLoading
          }
          onMembers={
            onMembers
          }
          compact
        />
      ) : null}

      <button
        type="button"
        onClick={() =>
          onEdit(club.id)
        }
        className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-medium text-white transition hover:bg-indigo-700"
        title="Modifier le club"
        aria-label={`Modifier ${
          club?.nom_club ||
          "le club"
        }`}
      >
        <FaPen />

        {!showMembers ? (
          <span>
            Modifier
          </span>
        ) : null}
      </button>

      <button
        type="button"
        onClick={() =>
          onDelete(club)
        }
        disabled={deleting}
        className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        title="Supprimer le club"
        aria-label={`Supprimer ${
          club?.nom_club ||
          "le club"
        }`}
      >
        {deleting ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
        ) : (
          <FaTrash />
        )}

        {!showMembers ? (
          <span>
            {deleting
              ? "Suppression…"
              : "Supprimer"}
          </span>
        ) : null}
      </button>
    </div>
  );
}